import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import { useLazyGetRewardsQuery } from '@/api/queries/rewards';
import { AppRoute } from '@/common/app/routes';
import { getRewardItemInfo } from '@/common/config/daily-reward-config';
import { useCountDownTimer } from '@/common/hooks/count-down-timer';
import { playSound } from '@/common/lib/soundEffect';
import { type Reward } from '@/common/types/earnRewardsType';
import BuyPaidPlan from '@/components/DailyReward/BuyPaidPlan/BuyPaidPlan';
import ClaimRewardOverlay from '@/components/DailyReward/ClaimRewardOverlay/ClaimRewardOverlay';
import DailyRewardBackground from '@/components/DailyReward/DailyRewardBackground/DailyRewardBackground';
import { Close } from '@/components/ui/Close/Close';
import { useModal } from '@/context/ModalContext';
import {
  checkLevelToGetRewards,
  getActiveReward,
  getIsBoughtPlan,
  getPaidRewardPlanCost,
} from '@/redux/selectors/earnRewardsSelector';
import { useAppSelector } from '@/redux/store';

import EarnRewardScreen from './EarnRewardScreen';

const EarnRewardsModal: React.FC = () => {
  const rewards = useAppSelector((state) => state.earnRewards.rewards?.rewards);
  const cost = useAppSelector(getPaidRewardPlanCost);
  const isBoughtPlan = useAppSelector(getIsBoughtPlan);

  const nextClaimTime = useAppSelector(
    (state) => state.earnRewards.rewards?.nextClaimTime,
  );

  const { earnRewardModal, setEarnRewardModal } = useModal();
  const [rewardAnimating, setRewardAnimating] = useState<Reward | null>(null);

  const [trigger] = useLazyGetRewardsQuery();

  const activeReward = useAppSelector(getActiveReward);
  const seconds = useCountDownTimer(nextClaimTime || 0);
  const congruentLevel = useAppSelector(checkLevelToGetRewards);
  const location = useLocation();

  const activeLocation: string[] = [AppRoute.EARN, AppRoute.MAIN];
  const allRewards =
    rewards && 'earnRewards' in rewards
      ? rewards.earnRewards
      : rewards && Array.isArray(rewards)
        ? rewards
        : undefined;

  useEffect(() => {
    if (congruentLevel && activeLocation.includes(location.pathname)) trigger();
  }, [congruentLevel, location.pathname]);

  const rewardAnimatingInfo = rewardAnimating
    ? getRewardItemInfo(rewardAnimating)
    : undefined;

  return (
    <>
      <div
        className={`modalRewardBackground ${earnRewardModal ? 'open' : ''} isolate`}
      >
        <CSSTransition
          in={earnRewardModal}
          timeout={300}
          classNames="my-node-modal"
          unmountOnExit
        >
          <>
            <div className="earn_reward_modal">
              <Close
                onClick={() => {
                  sessionStorage.setItem('isRewardClosed', 'true');
                  setEarnRewardModal(false);
                }}
                className="absolute right-4 top-4 z-[5] size-12"
                imageVariant="outline"
                variant="modal"
              />
              <DailyRewardBackground />
              {cost && !isBoughtPlan ? (
                <BuyPaidPlan cost={cost} rewards={allRewards} />
              ) : (
                <EarnRewardScreen
                  activeReward={activeReward}
                  allRewards={allRewards}
                  setRewardAnimating={setRewardAnimating}
                  seconds={seconds}
                />
              )}
            </div>
            {rewardAnimating && rewardAnimatingInfo && (
              <ClaimRewardOverlay
                {...rewardAnimatingInfo}
                {...rewardAnimatingInfo.img}
                onClose={() => {
                  playSound('buttons/switch');
                  setEarnRewardModal(false);
                  setRewardAnimating(null);
                }}
              />
            )}
          </>
        </CSSTransition>
      </div>
    </>
  );
};

export default EarnRewardsModal;
